import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { parseServerError } from "utils/errors";
import callApi from "helpers/callApi";
import {
  extractBulkDeleteResponsePayload,
  normalizeDeletedIds,
} from "./normalizeBulkDeleteResponse";
import handleErrorForThunk from "helpers/handleErrorForThunk";
import { baseURL } from "constants";
import { getCompanyReadiness } from "./documentsService";

export const createInvoice = createAsyncThunk(
  "documents/createInvoice",
  async ({ params, attachments = [] }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("data", JSON.stringify(params));
      for (let i = 0; i < attachments.length; i++) {
        formData.append(
          "attachments",
          attachments[i].file,
          attachments[i].file.name
        );
      }
      const response = await callApi("/documents/invoices", "POST", formData, {
        contentType: "multipart/form-data",
      });
      if (response.ok) {
        return await response.json();
      } else {
        try {
          const errorText = await response.text();
          const errorData = JSON.parse(errorText);
          return rejectWithValue(errorData);
        } catch (parseError) {
          return rejectWithValue({
            error: `HTTP ${response.status}: ${response.statusText}`,
            status: response.status
          });
        }
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const createExemption = createAsyncThunk(
  "documents/createExemption",
  async ({ params, attachments = [] }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("data", JSON.stringify(params));
      for (let i = 0; i < attachments.length; i++) {
        formData.append(
          "attachments",
          attachments[i].file,
          attachments[i].file.name
        );
      }
      const response = await callApi(
        "/documents/resale_certificate",
        "POST",
        formData,
        {
          contentType: "multipart/form-data",
        }
      );
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const getCustomersList = createAsyncThunk(
  "documents/getCustomersList",
  async (_, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams({
        limit: "1000",
        offset: "0",
      });
      const response = await callApi(`/documents/customers?${queryParams.toString()}`, "GET");
      if (response.ok) {
        const data = await response.json();
        return Array.isArray(data) ? data : (data?.items || data?.data || []);
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const addCustomerToList = createAsyncThunk(
  "documents/addCustomerToList",
  async (data, { rejectWithValue }) => {
    try {
      const res = await callApi("/documents/customer", "POST", data);
      const json = await res.json().catch(() => ({}));

      if (res.ok) return json;

      return json.error || "Unknown error";
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const sendingEinvoices = createAsyncThunk(
  "documents/sendingEinvoices",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/peppol/sending_einvoices", "POST", data);
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const editCustomerInList = createAsyncThunk(
  "documents/editCustomerInList",
  async (data, { rejectWithValue }) => {
    try {
      const res = await callApi("/documents/customer", "PUT", data);
      const json = await res.json().catch(() => ({}));

      if (res.ok) return json;

      return json.error || "Unknown error";
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const getBanksList = createAsyncThunk(
  "documents/getBanksList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/documents/bank", "GET");
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const addBankToList = createAsyncThunk(
  "documents/addBankToList",
  async (data, { rejectWithValue }) => {
    try {
      const res = await callApi("/documents/bank", "POST", data);
      const json = await res.json().catch(() => ({}));

      if (res.ok) return json;

      return json.error || "Unknown error";
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);


export const editBankInList = createAsyncThunk(
  "documents/editBankInList",
  async (data, { rejectWithValue }) => {
    try {
      const res = await callApi("/documents/bank", "PUT", data);

      const json = await res.json();
      if (res.ok) return json;

      return json.error || "Unknown error";
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);



export const getTaxList = createAsyncThunk(
  "documents/getTaxList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/documents/tax", "GET");
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const addTaxToList = createAsyncThunk(
  "documents/addTaxToList",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/documents/tax", "POST", data);
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const editTaxInList = createAsyncThunk(
  "documents/editTaxInList",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/documents/tax", "PUT", data);
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchDocuments = createAsyncThunk(
  "documents/fetchDocuments",
  async (data, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        type: data.type,
        invoice_type: data.invoice_type,
        offset: String(data.offset),
        limit: String(data.limit),
      });

      if (data.from_date) {
        params.set("from_date", data.from_date);
      }
      if (data.to_date) {
        params.set("to_date", data.to_date);
      }
      const searchTrimmed =
        data.search != null ? String(data.search).trim() : "";
      if (searchTrimmed) {
        params.set("search", searchTrimmed);
      }
      if (data.customer_id) {
        params.set("customer_id", data.customer_id);
      }
      if (Array.isArray(data.status)) {
        data.status
          .filter(Boolean)
          .forEach((s) => params.append("statuses", String(s)));
      } else if (data.status) {
        params.set("statuses", String(data.status));
      }

      const response = await callApi(
        `/documents?${params.toString()}`,
        "GET"
      );
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchCompanyReadiness = createAsyncThunk(
  "documents/fetchCompanyReadiness",
  async (_, { rejectWithValue }) => {
    try {
      const { response, data } = await getCompanyReadiness();

      if (response.ok) {
        return data;
      }

      return rejectWithValue(
        data?.error ? data : { error: `HTTP ${response.status}` }
      );
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

export const fetchDocumentsExemptionCertificates = createAsyncThunk(
  "documents/fetchDocumentsExemptionCertificates",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/documents/cert`,
        "GET"
      );
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchParametersForAddExemptionCertificates = createAsyncThunk(
  "documents/fetchParametersForAddExemptionCertificates",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/documents/get_data_for_cert`,
        "GET"
      );
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const postDataAddExemptionCertificates = createAsyncThunk(
  "documents/postDataAddExemptionCertificates",
  async ({ params, attachments = [] }, { rejectWithValue }) => {
    try {
      const paramsString = JSON.stringify(params);
      const response = await callApi("/documents/post_data_for_cert", "POST", paramsString);
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const deleteVatInvoice = createAsyncThunk(
  "documents/deleteVatInvoice",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(`/documents/${data.id}`, "DELETE");
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);
export const deleteVatInvoiceBulk = createAsyncThunk(
  "documents/deleteVatInvoiceBulk",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(`/documents/`,"DELETE", data);
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const getDataForExemptionCertificate = createAsyncThunk(
  "documents/getDataForExemptionCertificate",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/documents/get_data_for_exemption_certificate`,
        "GET"
      );
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const getInvoiceDetails = createAsyncThunk(
  "documents/getInvoiceDetails",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await callApi(`/documents/detail/${invoiceId}`, "GET");
      if (response.ok) {
        return await response.json();
      } else {
        const error = await response.text();
        const parsedError = JSON.parse(error);
        return rejectWithValue(parsedError);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const getInvoicesFromXero = createAsyncThunk(
  "documents/getInvoicesFromXero",
  async (data, { rejectWithValue }) => {
    try {
      let url = `/documents/xero?from_date=${data.from_date}&to_date=${data.to_date}&is_pdf=${data.is_pdf}&website_id=${data.website_id}`;

      if (data.customer) {
        url += `&customer=${encodeURIComponent(data.customer)}`;
      }
      if (data.country) {
        url += `&country=${data.country}`;
      }
      if (data.invoice_statuses && data.invoice_statuses.length > 0) {
        data.invoice_statuses.forEach(status => {
          url += `&invoice_statuses=${status}`;
        });
      }
      
      const response = await callApi(url, "GET");
      if (response.ok) {
        return await response.json();
      } else {
        const error = await response.text();
        const parsedError = JSON.parse(error);

        return rejectWithValue(parsedError);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const convertInvoicesToTransaction = createAsyncThunk(
  "documents/convertInvoicesToTransaction",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/documents/invoices_to_transaction?from_date=${data.from_date}&to_date=${data.to_date}`,
        "POST"
      );
      if (response.ok) {
        return await response.json();
      } else {
        const error = await response.text();
        const parsedError = JSON.parse(error);

        return rejectWithValue(parsedError);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const uploadDocuments = createAsyncThunk(
  "documents/uploadDocuments",
  async ({ files, invoiceType }, { rejectWithValue }) => {
    try {
      let url = `/documents/upload`;
      if (invoiceType) {
        url += `?invoice_type=${invoiceType}`;
      }
      
      const response = await callApi(url, "POST", files, {
        contentType: "multipart/form-data",
      });
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const uploadExcelDocuments = createAsyncThunk(
  "documents/uploadExcelDocuments",
  async ({ file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file, file.name);
      
      const response = await callApi("/documents/upload_excel", "POST", formData, {
        contentType: "multipart/form-data",
      });
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchDocumentsMessages = createAsyncThunk(
  "payment/fetchDocumentsMessages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/notify/get_system_message_active?models_name=documents",
        "GET"
      );
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchIntegrationsMessages = createAsyncThunk(
  "documents/fetchIntegrationsMessages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/notify/get_system_message_active?models_name=documents_integrations",
        "GET"
      );
      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const getBankCurrencyMapping = createAsyncThunk(
  "documents/getBankCurrencyMapping",
  async (currency, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/documents/banks_currency_mapping/${currency}`,
        "GET"
      );

      if (response.ok) {
        return await response.json();
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const deleteBank = createAsyncThunk(
  "banks/deleteBank",
  async (bankId, { rejectWithValue }) => {
    try {
      await callApi(
        `/documents/bank/${bankId}`,
        "DELETE"
      );
      return bankId;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Delete failed");
    }
  }
);

export const markInvoiceAsPaid = createAsyncThunk(
  "documents/markInvoiceAsPaid",
  async (arg, { rejectWithValue }) => {
    try {
      const body = Array.isArray(arg) ? { document_ids: arg } : arg;
      const res = await callApi(
        "/documents/invoice_status/mark_as_paid",
        "POST",
        body
      );

      const json = await res.json().catch(() => ({}));

      if (res.ok) return json;

      return rejectWithValue(json.error || json.message || "Unknown error");
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const createEdocFromInvoices = createAsyncThunk(
  "edocuments/createEdocFromInvoices",
  async (arg, { rejectWithValue }) => {
    try {
      const body = Array.isArray(arg) ? { invoice_ids: arg } : arg;

      const res = await callApi(
        "/edocuments/from-invoices",
        "POST",
        body
      );

      const json = await res.json().catch(() => ({}));

      if (res.ok) return json;

      return rejectWithValue(json.error || "Unknown server error");
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

// export const sendInvoicesByEmail = createAsyncThunk(
//   "documents/sendInvoicesByEmail",
//   async (invoiceIds, { rejectWithValue }) => {
//     try {
//       const ids = Array.isArray(invoiceIds) ? invoiceIds : [invoiceIds];

//       const body = {
//         invoice_ids: ids,
//       };

//       const res = await callApi(
//         "/documents/send_invoices_by_email",
//         "POST",
//         body
//       );

//       const json = await res.json().catch(() => ({}));

//       if (res.ok) return json;

//       return rejectWithValue(json.error || "Unknown server error");
//     } catch (err) {
//       return rejectWithValue("Server error");
//     }
//   }
// );

export const sendInvoicesViaEmail = createAsyncThunk(
  "documents/sendInvoicesByEmail",
  async (invoiceIds, { rejectWithValue }) => {
    try {
      const ids = Array.isArray(invoiceIds) ? invoiceIds : [invoiceIds];

      const body = {
        invoice_ids: ids,
      };

      const res = await callApi(
        "/documents/send_invoices_by_email",
        "POST",
        body
      );

      const json = await res.json().catch(() => ({}));

      if (res.ok) return json;

      return rejectWithValue(json.error || "Unknown server error");
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const deleteInvoices = createAsyncThunk(
  "documents/deleteInvoices",
  async (arg, { rejectWithValue }) => {
    try {
      const body = Array.isArray(arg) ? { invoice_ids: arg } : arg;
      const response = await callApi(
        "/documents/actions/",
        "DELETE",
        body
      );

      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        return rejectWithValue(json.error || json.message || response.status);
      }

      const responsePayload = extractBulkDeleteResponsePayload(json);
      return responsePayload ?? json;
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

const documentsSlice = createSlice({
  name: "documents",
  initialState: {
    invoice: {
      loading: {},
      error: null,
      previewUrl: "",
      invoiceNumber: null,
      invoiceId: null,
      details: null,
      detailsLoading: false,
      detailsError: null,
    },
    exemption: {
      loading: {},
      error: null,
      previewUrl: "",
    },
    customers: [],
    banks: [],
    taxes: [],
    documents: [],
    documentsExemptionCertificates: [],
    exemptionData: {},
    messages: [],
    integrationsMessages: [],
    fetchingIntegrationsMessages: false,
    fetchingIntegrationsMessagesError: null,
    countDocuments: 0,
    countDocumentsExemption: 0,
    parametersForAddExemptionCertificates: {},
    uploadingDocuments: false,
    uploadingDocumentsError: null,
    uploadingExcelDocuments: false,
    uploadingExcelDocumentsError: null,
    showCustomerValidationModal: false,
    customerValidationError: "",
    errorModal: {
      open: false,
      title: 'Error',
      description: '',
    },
    mappingLoading: false,
    mappingError: null,
    bankCurrencyMapping: null,
    markPaidMessage: null,
    markPaidError: null,
    markPaidLoading: false,
    createEdocLoading: false,
    createEdocMessage: null,
    createEdocError: null,
    createEdocInfo: null,
    sendInvoicesViaEmailLoading: false,
    sendInvoicesViaEmailMessage: null,
    sendInvoicesViaEmailError: null,
    companyReadinessLoading: false,
    companyReadiness: null,
    companyReadinessError: null,
    deleteInvoicesLoading: false,
  },
  reducers: {
    clearInvoiceError: (state) => {
      state.invoice.error = null;
    },
    clearInvoiceNumber: (state) => {
      state.invoice.invoiceNumber = null;
      state.invoice.invoiceId = null;
    },
    setShowCustomerValidationModal: (state, action) => {
      state.showCustomerValidationModal = action.payload;
    },
    setCustomerValidationError: (state, action) => {
      state.customerValidationError = action.payload;
    },
    showErrorModal: (state, action) => {
      const { title = 'Error', description = '' } = action.payload || {};
      state.errorModal = { open: true, title, description };
    },
    hideErrorModal: (state) => {
      state.errorModal.open = false;
    },
    clearMarkPaid(state) {
      state.markPaidMessage = null;
      state.markPaidError = null;
      state.markPaidLoading = false;
    },
    clearCreateEdoc(state) {
      state.createEdocMessage = null;
      state.createEdocError = null;
      state.createEdocInfo = null;
    },
    clearDocMessages(state) {
      state.messages = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createInvoice.pending, (state, action) => {
        const { action: type } = action.meta.arg.params;
        state.invoice.loading = {
          [type]: true,
        };
        state.invoice.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.invoice.loading = {};
        state.invoice.error = null;
        state.invoice.previewUrl = `${baseURL}/${action.payload.pdf_url}`;
        const { action: requestAction } = action.meta.arg.params;
        if (requestAction === "preview") {
          state.invoice.invoiceNumber = action.payload.invoice_number;
          state.invoice.invoiceId = action.payload.invoice_id;
        } else if (requestAction === "send") {
          state.invoice.invoiceNumber = null;
          state.invoice.invoiceId = null;
        }
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.invoice.loading = {};
        const errorMessage = action.payload?.error || action.payload?.message || JSON.stringify(action.payload);
        if (errorMessage && errorMessage.includes('Customer validation failed')) {
          // Don't set error for customer validation - it will be handled by modal
          state.invoice.error = null;
        } else {
          state.invoice.error = parseServerError(action.payload);
        }
      })
      .addCase(createExemption.pending, (state, action) => {
        const { action: type } = action.meta.arg.params;
        state.exemption.loading = {
          [type]: true,
        };
        state.exemption.error = null;
      })
      .addCase(createExemption.fulfilled, (state, action) => {
        state.exemption.loading = {};
        state.exemption.error = null;
        state.exemption.previewUrl = `${baseURL}/${action.payload.pdf_url}`;
      })
      .addCase(createExemption.rejected, (state, action) => {
        state.exemption.loading = {};
        state.exemption.error = parseServerError(action.payload);
      })
      .addCase(getCustomersList.pending, (state) => {
        state.getCustomersLoading = true;
        state.getCustomersError = null;
      })
      .addCase(getCustomersList.fulfilled, (state, action) => {
        state.getCustomersLoading = false;
        const payload = action.payload;
        state.customers = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
      })
      .addCase(getCustomersList.rejected, (state, action) => {
        state.getCustomersLoading = false;
        state.getCustomersError = parseServerError(action.payload);
      })
      .addCase(addCustomerToList.pending, (state) => {
        state.addingCustomerLoading = true;
        state.addingCustomerError = null;
      })
      .addCase(addCustomerToList.fulfilled, (state, action) => {
        state.addingCustomerLoading = false;
        state.addingCustomerError = null;
        state.customers.push(action.payload.customer);
      })
      .addCase(addCustomerToList.rejected, (state, action) => {
        state.addingCustomerLoading = false;
        state.addingCustomerError = parseServerError(action.payload);
      })
      .addCase(sendingEinvoices.pending, (state) => {
        state.sendingEinvoicesLoading = true;
        state.sendingEinvoicesError = null;
      })
      .addCase(sendingEinvoices.fulfilled, (state, action) => {
        state.sendingEinvoicesLoading = false;
        state.sendingEinvoicesError = null;
      })
      .addCase(sendingEinvoices.rejected, (state, action) => {
        state.sendingEinvoicesLoading = false;
        state.sendingEinvoicesError = parseServerError(action.payload);
      })
      .addCase(editCustomerInList.pending, (state) => {
        state.editingCustomerLoading = true;
        state.editingCustomerError = null;
      })
      .addCase(editCustomerInList.fulfilled, (state, action) => {
        state.editingCustomerLoading = false;
        state.editingCustomerError = null;
        const index = state.customers.findIndex(
          (customer) => customer.id === action.payload.customer.id
        );
        if (index !== -1) {
          state.customers[index] = action.payload.customer;
        }
      })
      .addCase(editCustomerInList.rejected, (state, action) => {
        state.editingCustomerLoading = false;
        state.editingCustomerError = parseServerError(action.payload);
      })
      .addCase(getBanksList.pending, (state) => {
        state.getBanksLoading = true;
        state.getBanksError = null;
      })
      .addCase(getBanksList.fulfilled, (state, action) => {
        state.getBanksLoading = false;
        state.banks = action.payload;
      })
      .addCase(getBanksList.rejected, (state, action) => {
        state.getBanksLoading = false;
        state.getBanksError = parseServerError(action.payload);
      })
      .addCase(addBankToList.pending, (state) => {
        state.addingBankLoading = true;
        state.addingBankError = null;
      })
      .addCase(addBankToList.fulfilled, (state, action) => { 
        state.addingBankLoading = false;
        state.addingBankError = null;
        state.banks.push(action.payload.bank);
      })
      .addCase(addBankToList.rejected, (state, action) => {
        state.addingBankLoading = false;
        state.addingBankError = parseServerError(action.payload);
      })
      .addCase(editBankInList.pending, (state) => {
        state.editingBankLoading = true;
        state.editingBankError = null;
      })

      .addCase(editBankInList.fulfilled, (state, action) => {
        state.editingBankLoading = false;
        state.editingBankError = null;
        // const index = state.banks.findIndex(
        //   (bank) => bank.id === action.payload.bank?.id
        // );
        // if (index !== -1) {
        //   state.banks[index] = action.payload.bank;
        // }
      })
      .addCase(editBankInList.rejected, (state, action) => {
        state.editingBankLoading = false;
        state.editingBankError = parseServerError(action.payload);
      })
      .addCase(getTaxList.pending, (state) => {
        state.getTaxLoading = true;
        state.getTaxError = null;
      })
      .addCase(getTaxList.fulfilled, (state, action) => {
        state.getTaxLoading = false;
        state.taxes = action.payload;
      })
      .addCase(getTaxList.rejected, (state, action) => {
        state.getTaxLoading = false;
        state.getTaxError = parseServerError(action.payload);
      })
      .addCase(addTaxToList.pending, (state) => {
        state.addingTaxLoading = true;
        state.addingTaxError = null;
      })
      .addCase(addTaxToList.fulfilled, (state, action) => {
        state.addingTaxLoading = false;
        state.addingTaxError = null;
        state.taxes.push(action.payload.tax);
      })
      .addCase(addTaxToList.rejected, (state, action) => {
        state.addingTaxLoading = false;
        state.addingTaxError = parseServerError(action.payload);
      })
      .addCase(editTaxInList.pending, (state) => {
        state.editingTaxLoading = true;
        state.editingTaxError = null;
      })
      .addCase(editTaxInList.fulfilled, (state, action) => {
        state.editingTaxLoading = false;
        state.editingTaxError = null;
        const index = state.taxes.findIndex(
          (tax) => tax.id === action.payload.tax.id
        );
        if (index !== -1) {
          state.taxes[index] = action.payload.tax;
        }
      })
      .addCase(editTaxInList.rejected, (state, action) => {
        state.editingTaxLoading = false;
        state.editingTaxError = parseServerError(action.payload);
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.fetchingDocuments = false;
        state.fetchingDocumentsError = null;
        state.documents = action.payload.items;
        state.countDocuments = action.payload.count;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.fetchingDocuments = false;
        state.fetchingDocumentsError = parseServerError(action.payload);
      })
      .addCase(fetchDocuments.pending, (state) => {
        state.fetchingDocuments = true;
        state.fetchingDocumentsError = null;
      })
      .addCase(fetchCompanyReadiness.pending, (state) => {
        state.companyReadinessLoading = true;
        state.companyReadinessError = null;
      })
      .addCase(fetchCompanyReadiness.fulfilled, (state, action) => {
        state.companyReadinessLoading = false;
        state.companyReadiness = action.payload;
        state.companyReadinessError = null;
      })
      .addCase(fetchCompanyReadiness.rejected, (state, action) => {
        state.companyReadinessLoading = false;
        state.companyReadinessError = parseServerError(action.payload);
      })
      .addCase(fetchDocumentsExemptionCertificates.fulfilled, (state, action) => {
        state.fetchingDocuments = false;
        state.fetchingDocumentsError = null;
        state.documentsExemptionCertificates = action.payload.items;
        state.countDocumentsExemption = action.payload.count;
      })
      .addCase(fetchDocumentsExemptionCertificates.rejected, (state, action) => {
        state.fetchingDocuments = false;
        state.fetchingDocumentsError = parseServerError(action.payload);
      })
      .addCase(fetchDocumentsExemptionCertificates.pending, (state) => {
        state.fetchingDocuments = true;
        state.fetchingDocumentsError = null;
      })
      .addCase(fetchParametersForAddExemptionCertificates.fulfilled, (state, action) => {
        state.fetchParametersForAddExemptionCertificates = false;
        state.fetchParametersForAddExemptionCertificatesError = null;
        // console.log('action.payload', action.payload)
        state.parametersForAddExemptionCertificates = action.payload.result;
      })
      .addCase(fetchParametersForAddExemptionCertificates.rejected, (state, action) => {
        state.fetchParametersForAddExemptionCertificates = false;
        state.fetchParametersForAddExemptionCertificatesError = parseServerError(action.payload);
      })
      .addCase(fetchParametersForAddExemptionCertificates.pending, (state) => {
        state.fetchParametersForAddExemptionCertificates = true;
        state.fetchParametersForAddExemptionCertificatesError = null;
      })
      .addCase(postDataAddExemptionCertificates.fulfilled, (state, action) => {
        state.postDataAddExemptionCertificates = false;
        state.postDataAddExemptionCertificatesError = null;
      })
      .addCase(postDataAddExemptionCertificates.rejected, (state, action) => {
        state.postDataAddExemptionCertificates = false;
        state.postDataAddExemptionCertificatesError = parseServerError(action.payload);
      })
      .addCase(postDataAddExemptionCertificates.pending, (state) => {
        state.postDataAddExemptionCertificates = true;
        state.postDataAddExemptionCertificatesError = null;
      })
      .addCase(deleteVatInvoice.fulfilled, (state, action) => {
        state.deleteVatInvoice = false;
        state.deleteVatInvoiceError = null;
      })
      .addCase(deleteVatInvoice.rejected, (state, action) => {
        state.deleteVatInvoice = false;
        state.deleteVatInvoiceError = parseServerError(action.payload);
      })
      .addCase(deleteVatInvoice.pending, (state) => {
        state.deleteVatInvoice = true;
        state.deleteVatInvoiceError = null;
      })
      .addCase(deleteVatInvoiceBulk.fulfilled, (state, action) => {
        state.deleteVatInvoiceBulk = false;
        state.deleteVatInvoiceBulkError = null;
      })
      .addCase(deleteVatInvoiceBulk.rejected, (state, action) => {
        state.deleteVatInvoiceBulk = false;
        state.deleteVatInvoiceBulkError = parseServerError(action.payload);
      })
      .addCase(deleteVatInvoiceBulk.pending, (state) => {
        state.deleteVatInvoiceBulk = true;
        state.deleteVatInvoiceBulkError = null;
      })
      .addCase(getDataForExemptionCertificate.fulfilled, (state, action) => {
        state.fetchingExemptionData = false;
        state.fetchingExemptionDataError = null;
        state.exemptionData = action.payload;
      })
      .addCase(getDataForExemptionCertificate.rejected, (state, action) => {
        state.fetchingExemptionData = false;
        state.fetchingExemptionDataError = parseServerError(action.payload);
      })
      .addCase(getDataForExemptionCertificate.pending, (state) => {
        state.fetchingExemptionData = true;
        state.fetchingExemptionDataError = null;
      })
      .addCase(getInvoiceDetails.pending, (state) => {
        state.invoice.detailsLoading = true;
        state.invoice.detailsError = null;
      })
      .addCase(getInvoiceDetails.fulfilled, (state, action) => {
        state.invoice.detailsLoading = false;
        state.invoice.detailsError = null;
        state.invoice.details = action.payload;
      })
      .addCase(getInvoiceDetails.rejected, (state, action) => {
        state.invoice.detailsLoading = false;
        state.invoice.detailsError = parseServerError(action.payload);
      })
      .addCase(getInvoicesFromXero.fulfilled, (state, action) => {
        state.fetchingInvoicesFromXero = false;
        state.fetchingInvoicesFromXeroError = null;
      })
      .addCase(getInvoicesFromXero.rejected, (state, action) => {
        state.fetchingInvoicesFromXero = false;
        state.fetchingInvoicesFromXeroError = action.payload;
      })
      .addCase(getInvoicesFromXero.pending, (state) => {
        state.fetchingInvoicesFromXero = true;
        state.fetchingInvoicesFromXeroError = null;
      })
      .addCase(convertInvoicesToTransaction.fulfilled, (state, action) => {
        state.fetchingConvertInvoicesToTransactionLoading = false;
        state.fetchingConvertInvoicesToTransactionLoadingError = null;
      })
      .addCase(convertInvoicesToTransaction.rejected, (state, action) => {
        state.fetchingConvertInvoicesToTransactionLoading = false;
        state.fetchingConvertInvoicesToTransactionLoadingError = action.payload;
      })
      .addCase(convertInvoicesToTransaction.pending, (state) => {
        state.fetchingConvertInvoicesToTransactionLoading = true;
        state.fetchingConvertInvoicesToTransactionLoadingError = null;
      })
      .addCase(uploadDocuments.fulfilled, (state, action) => {
        state.uploadingDocuments = false;
        state.uploadingDocumentsError = null;
      })
      .addCase(uploadDocuments.rejected, (state, action) => {
        state.uploadingDocuments = false;
        state.uploadingDocumentsError = action.payload;
      })
      .addCase(uploadDocuments.pending, (state) => {
        state.uploadingDocuments = true;
        state.uploadingDocumentsError = null;
      })
      .addCase(uploadExcelDocuments.fulfilled, (state, action) => {
        state.uploadingExcelDocuments = false;
        state.uploadingExcelDocumentsError = null;
      })
      .addCase(uploadExcelDocuments.rejected, (state, action) => {
        state.uploadingExcelDocuments = false;
        state.uploadingExcelDocumentsError = action.payload;
      })
      .addCase(uploadExcelDocuments.pending, (state) => {
        state.uploadingExcelDocuments = true;
        state.uploadingExcelDocumentsError = null;
      })
      .addCase(fetchDocumentsMessages.fulfilled, (state, action) => {
        state.fetchDocumentsMessages = false;
        state.fetchDocumentsMessagesError = null;
        state.messages = action.payload;
      })
      .addCase(fetchDocumentsMessages.rejected, (state, action) => {
        state.fetchDocumentsMessages = false;
        state.fetchDocumentsMessagesError = action.payload;
      })
      .addCase(fetchDocumentsMessages.pending, (state) => {
        state.fetchDocumentsMessages = true;
        state.fetchDocumentsMessagesError = null;
      })
      .addCase(fetchIntegrationsMessages.pending, (state) => {
        state.fetchingIntegrationsMessages = true;
        state.fetchingIntegrationsMessagesError = null;
      })
      .addCase(fetchIntegrationsMessages.fulfilled, (state, action) => {
        state.fetchingIntegrationsMessages = false;
        state.integrationsMessages = action.payload || [];
        state.fetchingIntegrationsMessagesError = null;
      })
      .addCase(fetchIntegrationsMessages.rejected, (state, action) => {
        state.fetchingIntegrationsMessages = false;
        state.fetchingIntegrationsMessagesError = action.payload;
      })
      .addCase(getBankCurrencyMapping.pending, (state) => {
        state.mappingLoading = true;
        state.mappingError = null;
        state.bankCurrencyMapping = null;
      })
      .addCase(getBankCurrencyMapping.fulfilled, (state, action) => {
        state.mappingLoading = false;
        state.bankCurrencyMapping = action.payload;
      })
      .addCase(getBankCurrencyMapping.rejected, (state, action) => {
        state.mappingLoading = false;
        state.mappingError = parseServerError(action.payload);
      })
      .addCase(deleteBank.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(deleteBank.fulfilled, (state, action) => {
        state.loading = false;
        state.banks = state.banks.filter(
          (b) => b.id !== action.payload
        );
      })

      .addCase(deleteBank.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error deleting bank";
      })

      .addCase(markInvoiceAsPaid.pending, (state) => {
        state.markPaidLoading = true;
        state.markPaidMessage = null;
        state.markPaidError = null;
      })
      .addCase(markInvoiceAsPaid.fulfilled, (state, action) => {
        state.markPaidLoading = false;
        state.markPaidMessage = action.payload?.message || "Marked as paid";
        
        const updatedIds = action.meta.arg;

        if (Array.isArray(updatedIds)) {
          updatedIds.forEach((id) => {
            const doc = state.documents?.find((d) => d.id === id);
            if (doc) {
              doc.status = "paid";
            }
          });
        }
      })
      .addCase(markInvoiceAsPaid.rejected, (state, action) => {
        state.markPaidLoading = false;
        state.markPaidError =
          action.payload || "Failed to mark invoice(s) as paid";
      })

      .addCase(createEdocFromInvoices.pending, (state, action) => {
        state.createEdocLoading = true;
        state.createEdocMessage = null;
        state.createEdocError = null;
        state.createEdocInfo = null;
      })
      .addCase(createEdocFromInvoices.fulfilled, (state, action) => {
        state.createEdocLoading = false;
        state.createEdocMessage = action?.payload?.success_message || null;
        state.createEdocError = action?.payload?.error_message || null;
        state.createEdocInfo = action?.payload?.skipped_message || null;
      })
      .addCase(createEdocFromInvoices.rejected, (state, action) => {
        state.createEdocLoading = false;
        state.createEdocError = action.payload || "Error creating e-document";
      })

      .addCase(deleteInvoices.pending, (state) => {
        state.deleteInvoicesLoading = true;
      })
      .addCase(deleteInvoices.fulfilled, (state, action) => {
        state.deleteInvoicesLoading = false;
        const deletedIds = normalizeDeletedIds(action.payload);
        if (deletedIds.length > 0) {
          state.documents = state.documents.filter(
            (doc) => !deletedIds.some((id) => id == doc.id)
          );
        }
      })
      .addCase(deleteInvoices.rejected, (state) => {
        state.deleteInvoicesLoading = false;
      })

      .addCase(sendInvoicesViaEmail.pending, (state) => {
        state.sendInvoicesViaEmailLoading = true;
        state.sendInvoicesViaEmailError = null;
        state.sendInvoicesViaEmailMessage = null;
      })
      .addCase(sendInvoicesViaEmail.fulfilled, (state, action) => {
        state.sendInvoicesViaEmailLoading = false;
        state.sendInvoicesViaEmailMessage = action.payload || "Sent successfully";
      })
      .addCase(sendInvoicesViaEmail.rejected, (state, action) => {
        state.sendInvoicesViaEmailLoading = false;
        state.sendInvoicesViaEmailError =
          action.payload || action.error?.message || "Server error";
      });
  },
});

export const { 
  clearInvoiceError, 
  clearInvoiceNumber,
  setShowCustomerValidationModal,
  setCustomerValidationError,
  showErrorModal,
  hideErrorModal,
  clearMarkPaid,
  clearCreateEdoc,
  clearDocMessages,
} = documentsSlice.actions;
export default documentsSlice.reducer;
